<template>
  <div>
    <back-nav :to="switchedCover ? 'SwitchCover' : 'PersonalDetailsReview'" />
    <v-card v-if="!pageLoading" class="tw-pb-12 tw-px-4">
      <card-title icon="mdi-autorenew">Review Vehicle Details</card-title>
      <div class="tw-px-0 md:tw-px-10 xl:tw-px-16 tw-mt-4 md:tw-mt-10">
        <v-form v-model="isValid">
          <div class="tw-flex tw-grid tw-grid-cols-1 md:tw-grid-cols-2">
            <v-text-field
                v-model="car.make"
                class="my-2 tw-px-0 md:tw-px-6"
                label="Vehicle Make"
                type="text"
                disabled
                :rules="[required('Vehicle Make')]"
            ></v-text-field>
            <v-text-field
                v-model="car.model"
                class="my-2 tw-px-0 md:tw-px-6"
                label="Vehicle Model"
                type="text"
                :rules="[required('Vehicle Model')]"
                disabled
            ></v-text-field>
            <vuetify-money
                v-if="coverType.covertypeId === 101"
                id="car-amount"
                v-model="car.valueOfCar"
                label="Value of Vehicle (Kshs.)"
                type="text"
                name="amount"
                class="mx-4"
                :rules="[required('Value of Vehicle (Kshs.)'), minAmount('Value of Vehicle (Kshs.)', 500000)]"
                :options="options"
                :disabled="!switchedCover"
            ></vuetify-money>
            <div v-if="coverType.covertypeId === 101">
              <v-autocomplete
                  id="year"
                  v-model="car.yearOfManufacture"
                  type="number"
                  :items="yearList"
                  :search-input.sync="yearSearch"
                  class="tw-w-full tw-mt-6 md:tw-mt-0 mx-4"
                  hide-no-data
                  hide-details
                  label="Year of Manufacture"
                  :rules="[required('Year of Manufacture'), v => parseInt(v,10) >= (new Date().getFullYear() - 12) || 'Minimum year for Comprehensive cover is 12 years']"
                  :disabled="true"
              ></v-autocomplete>
              <small :class="['tw-italic', parseInt(car.yearOfManufacture,10) >= (new Date().getFullYear() - 12) ? 'c-yellow-text' : 'tw-text-red-500']"> <v-icon size="14" :color="parseInt(car.yearOfManufacture,10) >= (new Date().getFullYear() - 12) ? 'secondary' : 'error'">mdi-information-outline</v-icon> Minimum year for Comprehensive cover is 12 years</small>
            </div>
            <v-text-field
                v-else
                v-model="car.yearOfManufacture"
                class="my-2 tw-px-0 md:tw-px-6"
                label="Year Of Manufacture"
                type="number"
                disabled
            ></v-text-field>
            <v-text-field
                v-model="car.registrationNumber"
                class="my-2 tw-px-0 md:tw-px-6"
                label="Registration Number"
                type="text"
                :rules="[required('Registration Number')]"
                disabled
            ></v-text-field>
            <v-text-field
                v-model="car.engineNumber"
                class="my-2 tw-px-0 md:tw-px-6"
                label="Engine No."
                type="text"
                :rules="[required('Engine No.')]"
                :disabled="coverType.car.engineNumber !== ''"
            ></v-text-field>
            <v-text-field
                v-model="car.logBookNumber"
                class="my-2 tw-px-0 md:tw-px-6"
                label="Logbook No. (optional)"
                type="text"
                :disabled="coverType.car.logBookNumber !== ''"
            ></v-text-field>
            <v-text-field
                v-model="car.cc"
                class="my-2 tw-px-0 md:tw-px-6"
                label="Engine Size (CC)"
                type="number"
                :rules="[required('Engine Size (CC)')]"
                :disabled="coverType.car.cc !== ''"
            ></v-text-field>
            <v-text-field
                v-model="car.color"
                class="my-2 tw-px-0 md:tw-px-6"
                label="Colour"
                type="text"
                :rules="[required('Colour')]"
                :disabled="coverType.car.color !== ''"
            ></v-text-field>
            <v-text-field
                v-model="car.seatingCapacity"
                class="my-2 tw-px-0 md:tw-px-6"
                label="Seating Capacity"
                type="number"
                required
                :rules="[min('Seating Capacity',1), max('Seating Capacity', 9)]"
                :disabled="coverType.car.seatingCapacity !== ''"
            ></v-text-field>
            <v-text-field
                v-model="car.chassisNumber"
                class="my-2 tw-px-0 md:tw-px-6"
                label="Chassis No."
                type="text"
                :rules="[required('Chassis No.'), minLength('Chassis No.', 10)]"
                :disabled="chassisDisabled"
            ></v-text-field>

          </div>

          <schedule-inspection v-if="coverType.covertypeId === 101" v-model="check" :scheduled="scheduled" :quote-id="quote.quoteId" :must-schedule="switchedCover" />

          <v-card-actions class="col-sm-4 offset-sm-4 tw-mt-5 md:tw-mt-8 tw-pb-8">
            <v-btn block large
                   color="secondary"
                   class="my-3 "
                   :loading="loading"
                   :disabled="!isValid"
                   @click="proceed">
              {{ isValid ? 'Continue' : 'Check if all fields are filled' }}
            </v-btn>
          </v-card-actions>

        </v-form>
      </div>
    </v-card>
    <v-card v-else class="tw-w-full tw-flex tw-justify-center tw-p-10">
      <v-progress-circular
          :size="50"
          color="primary"
          indeterminate
      ></v-progress-circular>
    </v-card>
  </div>
</template>

<script>
/* eslint-disable no-param-reassign,no-plusplus */
import BackNav from '@/components/buy/BackNav';
import validations from '@/utils/validations';
import CardTitle from '@/components/shared/CardTitle';
import axios from 'axios';
import { FB_RENEW_REVIEW_VEHICLE_DETAILS } from '@/utils/const';
import ScheduleInspection from '@/components/buy/ScheduleInspection';

export default {
  name: 'VehicleDetailsReview',
  props: {
    renewal: {
      type: Object,
    },
    switchedCover: {
      default: false,
      type: Boolean,
    },
  },
  components: { ScheduleInspection, CardTitle, BackNav },
  data() {
    return {
      loading: false,
      car: {
        chassisNumber: '',
      },
      pageLoading: true,
      ...validations,
      isValid: false,
      data: {},
      yearList: this.allowedYears(new Date().getFullYear() - 25),
      yearSearch: null,
      options: {
        locale: 'en',
        prefix: 'Kes',
        suffix: '',
        precision: 0,
      },
      scheduled: false,
      chassisDisabled: true,
    };
  },
  computed: {
    quote() {
      return this.$store.getters['renewal/fetchQoute'];
    },
    coverType() {
      return this.renewal.quoteDetails.covertypes.find((id) => id.selected === 'Y');
    },
  },
  mounted() {
    this.car = { ...this.coverType.car };
    this.car.yearOfManufacture = this.coverType.car.yearOfManufacture.toString();
    this.$store.dispatch('renewal/fetchQuotation', this.$route.params.batchNo).then(() => {
      this.pageLoading = false;
    });
    this.chassisDisabled = this.coverType.car.chassisNumber !== '' && this.car.chassisNumber.length >= 10;
  },
  methods: {
    proceed() {
      this.loading = true;
      axios.post(`/customer/quotation/renewal/saveCar/${this.renewal.batchId}`, { carList: [this.car] })
        .then((response) => {
          if (response.data.success) {
            this.$toast.success('Vehicle Details successfully updated', 'Vehicle Details');
            this.loading = false;
            this.$analytics.logEvent(FB_RENEW_REVIEW_VEHICLE_DETAILS, { product: 'Renewal Journey Review Vehicle Details' });
            this.$emit('to-policy-review', this.renewal);
          }
        });
    },
    allowedYears(startYear) {
      const currentYear = new Date().getFullYear();
      const years = [];
      startYear = startYear || 2000;
      while (startYear <= currentYear) {
        const newYear = startYear++;
        years.push(newYear.toString());
      }
      return years.reverse();
    },
  },
};
</script>
